import { Button, Tooltip, CircularProgress } from '@mui/material';

type Size = 's' | 'm';

function getClasses({ size, outlined }: { size: Size; outlined: boolean }) {
  const baseClasses = [
    'flex',
    'gap-8',
    'items-center',
    'bg-transparent',
    'hover:bg-grey-5',
    'rounded-4',
    'border',
    'border-solid',
    'font-600',
    'text-14',
    'text-typography-primary',
    'disabled:opacity-50',
    'disabled:bg-white',
    'transition-opacity',
    'transition-colors',
  ];

  let sizeClasses = ['px-16', 'py-12'];
  if (size === 's') sizeClasses = ['px-12', 'py-6'];

  const outlinedClasses = outlined ? ['border-border'] : ['border-transparent'];

  return [...baseClasses, ...sizeClasses, ...outlinedClasses].join(' ');
}

type TransparentButtonProps = {
  cb: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  disabledText?: string;
  label: string;
  inverted?: boolean;
  loading?: boolean;
  size?: Size;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  outlined?: boolean;
};

export default function TransparentButton({
  cb,
  disabled,
  loading,
  disabledText,
  label,
  icon,
  outlined = false,
  size = 's',
  children,
  ...props
}: TransparentButtonProps) {
  const classes = getClasses({ size, outlined });

  if (disabledText) {
    return (
      <Tooltip title={disabledText} arrow>
        <span>
          <Button disabled className={classes} {...props}>
            {icon}
            {children || label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button onClick={cb} disabled={disabled || loading} className={classes} {...props}>
      {loading ? <CircularProgress size={16} className="text-typography-primary" /> : icon}
      {children || label}
    </Button>
  );
}
